import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Globe, Info, Loader2 } from "lucide-react";
import { useI18n } from '@/hooks/useI18n';
import {
    MAIL_DEFAULT_LANGUAGE,
    MAIL_LANGUAGE_LABELS,
    type MailLanguage,
} from "@/lib/shared-proposal/hooks/useClientMailLanguage";

interface MailLanguageSelectorProps {
    /** Distingue gli id quando in pagina c'e' piu' di un selettore. */
    id?: string;
    disabled?: boolean;
    /** Lingua con cui partira' questa mail: sempre valorizzata. */
    value: MailLanguage;
    onChange: (value: MailLanguage) => void;
    /**
     * Lingua sul profilo del cliente. `null` solo quando il cliente non ha
     * ancora un profilo: `users.preferred_language` ha un default, quindi per
     * chi e' anagrafato c'e' sempre un valore.
     */
    profileLanguage: MailLanguage | null;
    isLoading?: boolean;
    /**
     * La lettura del profilo e' fallita: `profileLanguage` e' `null` ma non
     * vuol dire che il cliente non abbia un account.
     */
    lookupFailed?: boolean;
}

export const MailLanguageSelector = ({
    id = 'mail-language',
    disabled,
    value,
    onChange,
    profileLanguage,
    isLoading,
    lookupFailed,
}: MailLanguageSelectorProps) => {
    const { t } = useI18n();

    // Il testo deve dire esattamente quello che il select mostra.
    const hint = (() => {
        if (isLoading) {
            return {
                icon: 'spinner' as const,
                iconClass: 'animate-spin text-gray-400',
                textClass: 'text-gray-500',
                text: t('mail.languageChecking'),
            };
        }
        if (profileLanguage === null && lookupFailed) {
            return {
                icon: 'info' as const, iconClass: 'text-amber-600', textClass: 'text-amber-700',
                text: t('mail.languageLookupFailed', { language: MAIL_LANGUAGE_LABELS[value] }),
            };
        }
        if (profileLanguage === null) {
            return {
                icon: 'info' as const, iconClass: 'text-gray-400', textClass: 'text-gray-500',
                text: t('mail.languageNoProfile', { language: MAIL_LANGUAGE_LABELS[MAIL_DEFAULT_LANGUAGE] }),
            };
        }
        if (value !== profileLanguage) {
            return {
                icon: 'info' as const, iconClass: 'text-amber-600', textClass: 'text-amber-700',
                text: t('mail.languageOverridden', { profile: MAIL_LANGUAGE_LABELS[profileLanguage] }),
            };
        }
        return {
            icon: 'info' as const, iconClass: 'text-gray-400', textClass: 'text-gray-500',
            text: t('mail.languageFromProfile', { language: MAIL_LANGUAGE_LABELS[profileLanguage] }),
        };
    })();
    const HintIcon = hint.icon === 'spinner' ? Loader2 : Info;

    return (
        <div className="space-y-2">
            <Label htmlFor={id} className="text-sm font-medium flex items-center gap-2">
                <Globe className="h-4 w-4 text-gray-500" />
                {t('mail.language')}
            </Label>

            <Select
                value={value}
                onValueChange={(v) => onChange(v as MailLanguage)}
                disabled={disabled}
            >
                <SelectTrigger id={id} className="w-full">
                    <SelectValue />
                </SelectTrigger>
                <SelectContent>
                    <SelectItem value="it">{MAIL_LANGUAGE_LABELS.it}</SelectItem>
                    <SelectItem value="en">{MAIL_LANGUAGE_LABELS.en}</SelectItem>
                </SelectContent>
            </Select>

            <p className="text-xs flex items-start gap-1">
                <HintIcon className={`h-3 w-3 mt-0.5 flex-shrink-0 ${hint.iconClass}`} />
                <span className={hint.textClass}>{hint.text}</span>
            </p>
        </div>
    );
};
